import { gql, TypedDocumentNode } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CheckBoxFormField,
  DateFormField,
  InputFormField,
  PasswordFormField,
} from '@repo/shared-client/components/fields'
import {
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Skeleton,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { SFMapBoxSearchBoxInputTheme } from '@repo/shared-client/mapbox'
import { ClientOnly, createFileRoute, Link, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import SFInfoPopup from '@web/src/components/primatives/sf-info-popup/sf-info-popup'
import SFLocationSearchField from '@web/src/components/primatives/sf-location-search-field/sf-location-search-field'
import SFProfileNameField from '@web/src/components/primatives/sf-profile-name-field/sf-profile-name-field'
import { authQueryKeys } from '@web/src/hooks/use-auth'
import { useTurnstileWidgetSize } from '@web/src/hooks/use-turnstile-widget-size'
import { authClient } from '@web/src/lib/auth/auth-client'
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, PASSWORD_NUMBER_REGEX } from '@web/src/lib/auth/auth-const'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { MIN_PROFILE_NAME_LENGTH } from '@web/src/lib/graphql/profiles/constants'
import type {
  FindprofileByReferralCodeQuery,
  FindprofileByReferralCodeQueryVariables,
} from '@web/src/lib/graphql/types.docs.generated'
import { useTranslation } from '@web/src/lib/intl/react'
import { CURRENT_LEGAL_POLICY_VERSIONS } from '@web/src/lib/legal/policy-version'
import { validateDateOfBirth } from '@web/src/lib/utilsClient'
import { BetterAuthSessionData, SessionUser } from '@web/src/lib/yoga/yoga-auth-context'
import { useEffect, useRef, useState } from 'react'
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Turnstile, useTurnstile } from 'react-turnstile'
import Cookies from 'universal-cookie'
import { z } from 'zod'
import { signupPageHeadFromLoaderData } from './signup.seo'

export const FIND_PROFILE_BY_REFERRAL_CODE: TypedDocumentNode<
  FindprofileByReferralCodeQuery,
  FindprofileByReferralCodeQueryVariables
> = gql`
  query FindprofileByReferralCode($referralCode: String!) {
    profileByReferralCode: profileByReferralCode(referralCode: $referralCode) {
      id
    }
  }
`

const authFooterLinkClass = cn(buttonVariants({ variant: 'link', size: 'default' }))
const authInlineLegalLinkClass = 'inline font-semibold text-primary underline-offset-4 hover:underline'

export const Route = createFileRoute('/(public)/(main-layout)/auth/signup')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: Signup,
  head: () => signupPageHeadFromLoaderData(),
  validateSearch: z.object({
    referralCode: z.coerce.string().optional(),
  }),
  loaderDeps: ({ search: { referralCode } }) => ({ referralCode }),
  loader: async ({ context, deps }) => {
    const session: BetterAuthSessionData = context.queryClient.getQueryData(authQueryKeys.session)
    const currentUser = session?.user as SessionUser

    if (currentUser && deps.referralCode) {
      const { data: queryResult, error } = await context.apolloClient.query({
        query: FIND_PROFILE_BY_REFERRAL_CODE,
        variables: { referralCode: deps.referralCode },
        errorPolicy: 'all',
      })

      if (error) {
        console.error(error, 'Invalid referral code:', deps.referralCode)
        throw redirect({ to: '/' } as never)
      }

      if (queryResult?.profileByReferralCode?.id) {
        throw redirect({
          to: '/profile/$profileId/about',
          params: { profileId: queryResult.profileByReferralCode.id },
        } as never)
      }
    }
  },
})

function Signup() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const turnstile = useTurnstile()
  const { referralCode } = useSearch({ strict: false })

  const formSchema = z.object({
    profileName: z
      .string()
      .min(
        MIN_PROFILE_NAME_LENGTH,
        t('Profile name must be at least {{count}} characters', { count: MIN_PROFILE_NAME_LENGTH })
      ),
    email: z.email({ message: t('Please enter a valid email address') }),
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `${t('Password must be at least')} ${MIN_PASSWORD_LENGTH} ${t('characters long')}`)
      .max(MAX_PASSWORD_LENGTH, `${t('Password must be less than')} ${MAX_PASSWORD_LENGTH} ${t('characters long')}`)
      .refine((password) => PASSWORD_NUMBER_REGEX.test(password), {
        message: t('Password must contain at least one number.'),
      }),
    referralCode: z.string().optional(),
    dateOfBirth: z.date({ message: t('Invalid date provided') }).refine(
      (value) => {
        const result = validateDateOfBirth(value)
        return result === true
      },
      {
        message: t('You must be 18 years old to be a member of this site.'),
      }
    ),
    location: z
      .object({
        name: z.string().nullable(),
        latitude: z.number().nullable(),
        longitude: z.number().nullable(),
      })
      .optional()
      .refine(
        (value) => value !== undefined && value?.latitude !== null && value?.longitude !== null && value?.name !== null,
        {
          message: t('You must provide your location for distance calculations'),
        }
      ),
    acceptedLegalPolicies: z.boolean().refine((value) => value === true, {
      message: t(
        'You must confirm you are 18 years of age or older and agree to the Terms of Use and Acceptable Use Policy.'
      ),
    }),
  })

  type FormValues = z.infer<typeof formSchema>

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      referralCode: referralCode ?? '',
      acceptedLegalPolicies: false,
    },
  })

  const acceptedLegalPolicies = useWatch({
    control: formMethods.control,
    name: 'acceptedLegalPolicies',
    defaultValue: false,
  })

  const [showSpinner, setShowSpinner] = useState(false)

  const [token, setToken] = useState('')
  const turnstileSize = useTurnstileWidgetSize()

  const cookies = new Cookies()

  const isCaptchaDisabled = getAppConfig().CAPTCHA_DISABLE === true

  const nameRef = useRef<HTMLInputElement>(null)
  const referralInputRef = useRef<HTMLInputElement>(null)
  const [showReferralField, setShowReferralField] = useState(Boolean(referralCode))

  useEffect(() => {
    nameRef.current?.focus()
  }, [])

  useEffect(() => {
    if (referralCode) {
      formMethods.setValue('referralCode', referralCode)
      setShowReferralField(true)
    }
  }, [referralCode, formMethods])

  useEffect(() => {
    if (showReferralField && !referralCode) {
      referralInputRef.current?.focus()
    }
  }, [showReferralField, referralCode])

  const onSubmit: SubmitHandler<FormValues> = async ({
    profileName,
    email,
    password,
    referralCode: referralCodeFrom,
    dateOfBirth,
    location,
  }) => {
    setShowSpinner(true)

    if (!(isCaptchaDisabled || token)) {
      setShowSpinner(false)
      return toast.error(t('Captcha is required'))
    }

    try {
      if (!isCaptchaDisabled) {
        cookies.set('captcha', token, { path: '/' })
      }

      const result = await authClient.signUp.email({
        name: profileName,
        email,
        password,
        referralCode: referralCodeFrom,
        dateOfBirth: dateOfBirth.toISOString(),
        location: location.name,
        locationLat: location.latitude,
        locationLong: location.longitude,
        termsOfUseAcceptedVersion: CURRENT_LEGAL_POLICY_VERSIONS.termsOfUse,
        acceptableUsePolicyAcceptedVersion: CURRENT_LEGAL_POLICY_VERSIONS.acceptableUsePolicy,
        fetchOptions: {
          headers: {
            'x-captcha-response': token,
          },
        },
      })

      if (result.error) {
        console.error(result)
        const isDuplicateEmail =
          result.error.code === 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL' || result.error.code === 'USER_ALREADY_EXISTS'
        const message = isDuplicateEmail
          ? t('This email address is already in use')
          : (result.error.message ?? t('Failed to create account'))

        if (isDuplicateEmail) {
          formMethods.setError('email', { message })
        }

        toast.error(message)
      } else {
        toast.success(t('Account created successfully'))

        if (!isCaptchaDisabled) {
          cookies.remove('captcha', { path: '/' })
        }

        navigate({ to: '/auth/verification-email-sent', search: { email } } as never)
      }
    } catch (err) {
      toast.error((err as Error)?.message ?? t('An error occurred'))
    } finally {
      if (!isCaptchaDisabled && turnstile && token) {
        setToken('')
        turnstile.reset()
      }
      setShowSpinner(false)
    }
  }

  return (
    <div className="relative z-10 flex justify-center py-6">
      <Card className="mx-4 w-full max-w-md shadow-2xl">
        <CardHeader className="flex flex-col items-center gap-2 text-center">
          <LogoFull size="md" />
          <h1 className="mt-2 text-center font-semibold text-lg text-muted-foreground">{t('Create your account')}</h1>
        </CardHeader>

        <CardContent>
          <Form formMethods={formMethods} onSubmit={onSubmit} suppressHydrationWarning>
            <div className="flex flex-col gap-4">
              <SFProfileNameField label={t('Choose a Profile Name')} name="profileName" ref={nameRef} required />

              <InputFormField
                autoComplete="email"
                label={t('Email Address')}
                labelEnd={
                  <SFInfoPopup
                    description={t(
                      'We use your email to create your account and send verification and account emails. We will not share your email with anyone.'
                    )}
                    iconButtonProps={{ 'aria-label': t('Why we ask for your email') }}
                    title={t('Why we ask for your email')}
                    trigger={t('Why do we ask for this?')}
                  />
                }
                name="email"
                placeholder={t('Enter your email')}
                required
              />

              <PasswordFormField
                autoComplete="new-password"
                label={t('Password')}
                maxPasswordLength={MAX_PASSWORD_LENGTH}
                minPasswordLength={MIN_PASSWORD_LENGTH}
                name="password"
                passwordNumberRegex={PASSWORD_NUMBER_REGEX}
                placeholder={t('Enter your password')}
                required
                showValidator
              />

              <DateFormField
                label={t('Date of Birth')}
                labelEnd={
                  <SFInfoPopup
                    description={t('We use your date of birth to confirm you are older than 18, to show your age.')}
                    iconButtonProps={{ 'aria-label': t('Why we ask for your date of birth') }}
                    title={t('Why we ask for your date of birth')}
                    trigger={t('Why do we ask for this?')}
                  />
                }
                name="dateOfBirth"
                required
              />

              <SFLocationSearchField
                enableGeolocation={false}
                label={t('Your Location')}
                labelEnd={
                  <SFInfoPopup
                    description={t('We use your location to calculate distances to events and other members.')}
                    iconButtonProps={{ 'aria-label': t('Why we ask for your location') }}
                    title={t('Why we ask for your location')}
                    trigger={t('Why do we ask for this?')}
                  />
                }
                name="location"
                placeholder={t('Enter your city')}
                required
                theme={SFMapBoxSearchBoxInputTheme}
              />

              {showReferralField ? (
                <InputFormField
                  disabled={!!referralCode}
                  forceUppercase
                  helperText={referralCode ? undefined : t('Enter your friends referral code if you have one')}
                  label={referralCode ? t('Referral Code') : t('Referral Code (Optional)')}
                  name="referralCode"
                  placeholder={t("Leave blank if you don't have a referral code")}
                  ref={referralInputRef}
                />
              ) : (
                <Button
                  className="self-center text-muted-foreground"
                  onClick={() => setShowReferralField(true)}
                  type="button"
                  variant="link"
                >
                  {t('Have a referral code?')}
                </Button>
              )}

              <CheckBoxFormField name="acceptedLegalPolicies" required>
                {t('I am 18 years of age or older and I agree to the')}{' '}
                <Link
                  className={authInlineLegalLinkClass}
                  rel="noopener noreferrer"
                  target="_blank"
                  to="/helpcenter/legal/terms-of-use"
                >
                  {t('Terms of Use')}
                </Link>{' '}
                {t('and')}{' '}
                <Link
                  className={authInlineLegalLinkClass}
                  rel="noopener noreferrer"
                  target="_blank"
                  to="/helpcenter/legal/acceptable-use-policy"
                >
                  {t('Acceptable Use Policy')}
                </Link>
              </CheckBoxFormField>

              <p className="text-muted-foreground text-sm leading-snug">
                {t(
                  'By creating an account, you acknowledge that your personal data will be processed in accordance with our'
                )}{' '}
                <Link
                  className={authInlineLegalLinkClass}
                  rel="noopener noreferrer"
                  target="_blank"
                  to="/helpcenter/privacy-policy"
                >
                  {t('Privacy Policy')}
                </Link>
              </p>

              {!isCaptchaDisabled && (
                <ClientOnly fallback={<Skeleton className="h-[65px] w-full" />}>
                  <div className={cn('flex justify-center', turnstileSize === 'compact' ? 'pb-4' : 'pb-0')}>
                    <Turnstile
                      appearance="always"
                      fixedSize={true}
                      onVerify={(props) => setToken(props)}
                      sitekey={getAppConfig().CAPTCHA_SITEKEY}
                      size={turnstileSize}
                    />
                  </div>
                </ClientOnly>
              )}

              <Button className="w-full" disabled={showSpinner || !acceptedLegalPolicies} size="lg" type="submit">
                {showSpinner ? <Spinner className="mr-2" /> : null}
                {t('Sign Up')}
              </Button>
            </div>
          </Form>
        </CardContent>

        <CardFooter className="justify-center">
          <p className="text-center text-md text-muted-foreground">
            {t('Already have an account?')}{' '}
            <Link className={authFooterLinkClass} to="/auth/login">
              {t('Login')}
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  )
}
